import { useState, type FormEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { firstRelation } from "@/lib/relations";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/base/PageHeader";
import { StatusBadge } from "@/components/base/StatusBadge";
import { Skeleton } from "@/components/base/Skeleton";
import { TableSkeleton } from "@/components/base/TableSkeleton";
import { EmptyState } from "@/components/base/EmptyState";
import { Modal } from "@/components/base/Modal";
import { downloadCsv, toCsv } from "@/lib/csv";
import Pagination from "@/components/base/Pagination";
import { useBulkSelection } from "@/components/feature/useBulkSelection";
import { BulkActionBar } from "@/components/feature/BulkActionBar";
import StagedImportModal from "@/components/feature/StagedImportModal";
import { parseProductsCsv, executeProductsImport, exportProductsFullCsv } from "@/lib/imports";
import { canManageProducts } from "@/lib/permissions";
import * as productApi from "@/lib/api/products";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { flattenWithDepth, findDuplicateCategory, type Category } from "@/lib/categories";
import { money } from "@/lib/format";
import { buildProductListRow, type ProductListRowViewModel } from "@/lib/product-list";
import { computeChannelCoverage, channelCoverageSummary, productMatchesChannelFilter, isSellingProvider, orderSellingConnectors, type CatalogueChannelFilter } from "@/lib/channel-coverage";
import { connectorDisplayName } from "@/lib/connector-identity";
import { ChannelMatrixCell } from "@/components/feature/ChannelCoverageCell";
import { indexBcListingStorefrontPaths, indexConnectorStorefront } from "@/lib/listing-live-display";
import { QuickStockModal } from "@/pages/products/components/QuickStockModal";
import { QuickVariantPriceModal } from "@/pages/products/components/QuickVariantPriceModal";

interface ProductRow {
  id: string;
  name: string;
  reference: string | null;
  sku: string;
  status: string;
  selling_price: number;
  cost_price: number | null;
  reorder_point: number;
  reorder_qty: number;
  barcode: string | null;
  mpn: string | null;
  brand: string | null;
  weight_kg: number | null;
  product_type: string | null;
  category_id?: string | null;
  categories?: { name: string } | null;
}

interface BalanceRow { product_id: string; on_hand: number; available: number; allocated: number; }

interface SupplierRow { id: string; name: string; code: string | null; status: string; }

type CategoryRow = Category;

const sanitize = (q: string) => q.replace(/[,%_()]/g, "").trim();

const inputCls =
  "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";
const labelCls = "block text-xs font-medium text-slate-500 mb-1";

function ProductsLoading() {
  return (
    <div>
      <PageHeader title="Products" subtitle="Manage your product catalogue" />
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <Skeleton className="w-72 h-9" />
        </div>
        <TableSkeleton columns={10} rows={10} />
      </div>
    </div>
  );
}

export default function Products() {
  const { org, role } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const [importOpen, setImportOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [catSaving, setCatSaving] = useState(false);
  const [catError, setCatError] = useState("");
  const [newCatName, setNewCatName] = useState("");
  const [newCatParentId, setNewCatParentId] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [statusFilter, setStatusFilter] = useState<"active" | "archived">("active");
  const [typeFilter, setTypeFilter] = useState("");
  const [supplierFilter, setSupplierFilter] = useState("");
  const [stockFilter, setStockFilter] = useState<"all" | "in_stock" | "low_stock" | "out_of_stock">("all");
  const [channelFilter, setChannelFilter] = useState<CatalogueChannelFilter>("all");
  const [quickStock, setQuickStock] = useState<{ productId: string; name: string; available: number; variantCount: number } | null>(null);
  const [quickPrices, setQuickPrices] = useState<{ productId: string; name: string } | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState("");
  const [bulkSellingOpen, setBulkSellingOpen] = useState(false);
  const [bulkSellingAction, setBulkSellingAction] = useState<"pause" | "resume">("pause");
  const [bulkCatOpen, setBulkCatOpen] = useState(false);
  const [bulkCategoryId, setBulkCategoryId] = useState("");
  const [bulkSupplierOpen, setBulkSupplierOpen] = useState(false);
  const [bulkSupplierId, setBulkSupplierId] = useState("");
  const [bulkReorderOpen, setBulkReorderOpen] = useState(false);
  const [bulkReorderPoint, setBulkReorderPoint] = useState("");
  const [bulkReorderQty, setBulkReorderQty] = useState("");
  const [bulkPriceOpen, setBulkPriceOpen] = useState(false);
  const [bulkPriceMode, setBulkPriceMode] = useState<"percentage" | "fixed">("percentage");
  const [bulkPriceValue, setBulkPriceValue] = useState("");

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["products", org?.id, debouncedSearch, page, pageSize, statusFilter, catFilter, typeFilter, supplierFilter, stockFilter],
    enabled: !!org?.id,
    queryFn: async () => {
      // Supplier and stock-state filters resolve to an explicit product-id set
      // up front (semi-join), so server-side pagination + count stay correct.
      let supplierIds: string[] | null = null;
      let stockIds: string[] | null = null;
      if (supplierFilter) {
        const { data: sp } = await supabase
          .from("supplier_products")
          .select("product_id")
          .eq("org_id", org!.id)
          .eq("supplier_id", supplierFilter);
        supplierIds = Array.from(new Set((sp ?? []).map((r) => r.product_id as string)));
      }
      if (stockFilter !== "all") {
        const [balRes, prodRes] = await Promise.all([
          supabase.from("inventory_balances").select("product_id, on_hand, available, allocated").eq("org_id", org!.id),
          supabase.from("products").select("id, reorder_point").eq("org_id", org!.id),
        ]);
        const onHand: Record<string, number> = {};
        const avail: Record<string, number> = {};
        for (const b of (balRes.data ?? []) as { product_id: string; on_hand: number; available: number }[]) {
          onHand[b.product_id] = (onHand[b.product_id] ?? 0) + Number(b.on_hand ?? 0);
          avail[b.product_id] = (avail[b.product_id] ?? 0) + Number(b.available ?? 0);
        }
        const ids: string[] = [];
        for (const pr of (prodRes.data ?? []) as { id: string; reorder_point: number }[]) {
          const oh = onHand[pr.id] ?? 0;
          const a = avail[pr.id] ?? 0;
          const rp = Number(pr.reorder_point ?? 0);
          const ok = stockFilter === "out_of_stock" ? (pr.id in onHand && oh <= 0) : stockFilter === "low_stock" ? oh > 0 && a <= rp : a > rp;
          if (ok) ids.push(pr.id);
        }
        stockIds = ids;
      }
      const noMatches =
        (supplierFilter && (supplierIds?.length ?? 0) === 0) ||
        (stockFilter !== "all" && (stockIds?.length ?? 0) === 0);

      let q = supabase
        .from("products")
        .select("id, name, reference, sku, status, selling_price, cost_price, reorder_point, reorder_qty, barcode, mpn, product_type, category_id, brand, weight_kg, categories(name)", { count: "exact" })
        .eq("org_id", org!.id)
        .is("retired_at", null);
      if (statusFilter === "archived") {
        q = q.eq("status", "archived");
      } else {
        q = q.in("status", ["active", "paused", "draft"]);
      }
      if (catFilter) q = q.eq("category_id", catFilter);
      if (typeFilter) q = q.eq("product_type", typeFilter);
      if (supplierIds && supplierIds.length > 0) q = q.in("id", supplierIds);
      if (stockIds && stockIds.length > 0) q = q.in("id", stockIds);

      // Search is variant-aware and identifier-aware: a variant SKU/barcode,
      // or a GTIN/UPC/EAN/MPN identifier, must surface its parent product.
      // We also record *why* each product matched so the UI can say so.
      const variantReasons: Record<string, string> = {};
      const identifierReasons: Record<string, string> = {};
      const term = sanitize(debouncedSearch);
      if (term && !noMatches) {
        const ilike = `%${term}%`;
        const t = term.toLowerCase();
        const [variantRes, identRes] = await Promise.all([
          supabase
            .from("variants")
            .select("product_id, sku, barcode")
            .eq("org_id", org!.id)
            .or(`sku.ilike.${ilike},barcode.ilike.${ilike}`),
          supabase
            .from("product_identifiers")
            .select("product_id, variant_id, type, value")
            .eq("org_id", org!.id)
            .ilike("value", ilike),
        ]);

        const matchedProductIds = new Set<string>();
        for (const v of (variantRes.data ?? []) as { product_id: string; sku: string | null; barcode: string | null }[]) {
          matchedProductIds.add(v.product_id);
          const viaSku = (v.sku ?? "").toLowerCase().includes(t);
          variantReasons[v.product_id] = viaSku ? `Variant SKU ${v.sku}` : `Variant barcode ${v.barcode}`;
        }

        // Identifiers may hang off a product directly, or off a variant.
        const identRows = (identRes.data ?? []) as { product_id: string | null; variant_id: string | null; type: string; value: string }[];
        const unresolvedVariantIds = identRows.filter((r) => !r.product_id && r.variant_id).map((r) => r.variant_id as string);
        let productIdByVariantId: Record<string, string> = {};
        if (unresolvedVariantIds.length > 0) {
          const vRes = await supabase
            .from("variants")
            .select("id, product_id")
            .eq("org_id", org!.id)
            .in("id", unresolvedVariantIds);
          for (const r of (vRes.data ?? []) as { id: string; product_id: string }[]) {
            productIdByVariantId[r.id] = r.product_id;
          }
        }
        for (const r of identRows) {
          const pid = r.product_id ?? (r.variant_id ? productIdByVariantId[r.variant_id] : null);
          if (pid) {
            matchedProductIds.add(pid);
            identifierReasons[pid] = `${r.type} ${r.value}`;
          }
        }

        const direct = `name.ilike.${ilike},sku.ilike.${ilike},barcode.ilike.${ilike},reference.ilike.${ilike},mpn.ilike.${ilike}`;
        const extraIds = Array.from(matchedProductIds);
        q = q.or(extraIds.length > 0 ? `${direct},id.in.(${extraIds.join(",")})` : direct);
      }

      let products: ProductRow[] = [];
      let totalCount = 0;
      if (!noMatches) {
        q = q.order("name").range(page * pageSize, page * pageSize + pageSize - 1);
        const productsRes = await q;
        products = (productsRes.data ?? []).map((row) => ({
          ...row,
          categories: firstRelation(row.categories),
        })) as ProductRow[];
        totalCount = productsRes.count ?? 0;
      }
      const ids = products.map((p) => p.id);
      const [balancesRes, categoriesRes, catCountsRes, variantsRes, suppliersRes] = await Promise.all([
        ids.length ? supabase.from("inventory_balances").select("product_id, on_hand, available, allocated").eq("org_id", org!.id).in("product_id", ids) : Promise.resolve({ data: [] }),
        supabase.from("categories").select("id, name, parent_id, status").eq("org_id", org!.id).order("name"),
        productApi.getCategoryProductCounts(org!.id),
        ids.length ? supabase.from("variants").select("product_id, sku, status, selling_price").eq("org_id", org!.id).in("product_id", ids) : Promise.resolve({ data: [] }),
        supabase.from("suppliers").select("id, name, code, status").eq("org_id", org!.id).order("name"),
      ]);
      const categoryCounts: Record<string, number> = {};
      (catCountsRes ?? []).forEach((r: { category_id: string; cnt: number }) => {
        if (r.category_id) categoryCounts[r.category_id] = Number(r.cnt);
      });

      // Per-product variant summary: 0/1 variant → its SKU, >1 → "N variants".
      const variantByProduct: Record<string, { count: number; sku: string | null }> = {};
      for (const v of (variantsRes.data ?? []) as { product_id: string; sku: string | null; status: string }[]) {
        const entry = variantByProduct[v.product_id] ?? { count: 0, sku: null };
        if (v.status !== "archived") {
          entry.count += 1;
          if (entry.count === 1) entry.sku = v.sku;
        }
        variantByProduct[v.product_id] = entry;
      }

      // Per-product variant selling prices, for a truthful list price: a
      // multi-variant product is summarised as a min–max range, never a single
      // (possibly null → "$0.00") product-level figure.
      const variantPricesByProduct: Record<string, (number | null)[]> = {};
      for (const v of (variantsRes.data ?? []) as { product_id: string; selling_price: number | null }[]) {
        (variantPricesByProduct[v.product_id] ??= []).push(v.selling_price ?? null);
      }

      return {
        products,
        total: totalCount,
        balances: (balancesRes.data ?? []) as BalanceRow[],
        categories: (categoriesRes.data ?? []) as CategoryRow[],
        categoryCounts,
        variantByProduct,
        variantPricesByProduct,
        variantReasons,
        identifierReasons,
        suppliers: (suppliersRes.data ?? []) as SupplierRow[],
      };
    },
  });

  const { data: coverageData } = useQuery({
    queryKey: ["catalogue-coverage", org?.id],
    enabled: !!org?.id,
    queryFn: async () => {
      const [connectors, listings, bc, drafts, locations, bcConfig] = await Promise.all([
        supabase.from("connectors").select("id, name, provider, auth_status").eq("org_id", org!.id),
        supabase.from("channel_listings").select("id, product_id, connector_id, publication_status, status, last_error, dirty_domains, external_listing_id").eq("org_id", org!.id),
        supabase.from("bigcommerce_products").select("listing_id, product_id, external_product_id, storefront_custom_url").eq("org_id", org!.id),
        supabase.from("product_wizard_drafts").select("id, title, updated_at, step").eq("org_id", org!.id).order("updated_at", { ascending: false }),
        supabase.from("locations").select("id, name").eq("org_id", org!.id).order("name"),
        supabase.from("connector_bigcommerce_config").select("connector_id, storefront_origin, store_hash").eq("org_id", org!.id),
      ]);
      const bcByListing: Record<string, string> = {};
      for (const row of (bc.data ?? []) as { listing_id: string; external_product_id: number | null }[]) {
        if (row.external_product_id) bcByListing[row.listing_id] = String(row.external_product_id);
      }
      const bcPathByListing = indexBcListingStorefrontPaths(
        (bc.data ?? []) as { listing_id: string; storefront_custom_url: string | null }[],
      );
      return {
        connectors: (connectors.data ?? []) as { id: string; name: string; provider: string; auth_status: string }[],
        listings: ((listings.data ?? []) as {
          id: string;
          product_id: string;
          connector_id: string;
          publication_status: string | null;
          status: string | null;
          last_error: string | null;
          dirty_domains: string[] | null;
          external_listing_id: string | null;
        }[]).map((l) => ({
          ...l,
          confirmedId: bcByListing[l.id] ?? l.external_listing_id,
        })),
        drafts: (drafts.data ?? []) as { id: string; title: string; updated_at: string; step: string }[],
        locations: (locations.data ?? []) as { id: string; name: string }[],
        storefront: indexConnectorStorefront(
          (bcConfig.data ?? []) as { connector_id: string; storefront_origin: string | null; store_hash: string | null }[],
        ),
        bcPathByListing,
      };
    },
  });

  const canManage = canManageProducts(role);
  const bulk = useBulkSelection<ProductRow>(data?.products ?? []);

  const runBulk = async (action: string, params: Record<string, unknown> = {}) => {
    setBulkBusy(true);
    setBulkResult("");
    let r: productApi.BulkUpdateResult;
    try {
      r = await productApi.bulkUpdate(org!.id, bulk.selectedIds, action, params);
    } catch (e) {
      setBulkBusy(false);
      setBulkResult((e as Error).message);
      return;
    }
    setBulkBusy(false);
    const failedCount = r.failed?.length ?? 0;
    const changes = r.changes ?? [];
    let msg = `${r.succeeded} updated${failedCount ? `, ${failedCount} failed` : ""}.`;
    if (changes.length > 0) {
      msg += ` Prices: ${changes.map((c) => `${c.old ?? "—"} → ${c.new ?? "—"}`).join(", ")}.`;
    }
    setBulkResult(msg);
    bulk.clear();
    queryClient.invalidateQueries({ queryKey: ["products", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["dashboard", org?.id] });
  };

  const runBulkSelling = async () => {
    setBulkBusy(true);
    setBulkResult("");
    let complete = 0;
    let partial = 0;
    let failed = 0;
    for (const productId of bulk.selectedIds) {
      try {
        const result = await productApi.setSellingState(org!.id, productId, bulkSellingAction);
        if (result.failures.length > 0 || result.pending.length > 0) partial += 1;
        else complete += 1;
      } catch {
        failed += 1;
      }
    }
    setBulkBusy(false);
    setBulkSellingOpen(false);
    setBulkResult(
      `${bulkSellingAction === "pause" ? "Pause" : "Resume"}: ${complete} completed`
      + (partial ? `, ${partial} need channel attention` : "")
      + (failed ? `, ${failed} failed` : "")
      + ".",
    );
    bulk.clear();
    queryClient.invalidateQueries({ queryKey: ["products", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["catalogue-coverage", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["dashboard", org?.id] });
  };

  // Restore has no dedicated bulk RPC action, so we drive the trusted
  // `set_product_status` RPC per product (archived → active).
  const runBulkRestore = async () => {
    setBulkBusy(true);
    setBulkResult("");
    let ok = 0;
    let fail = 0;
    for (const id of bulk.selectedIds) {
      try {
        await productApi.setStatus(org!.id, id, "active");
        ok += 1;
      } catch {
        fail += 1;
      }
    }
    setBulkBusy(false);
    setBulkResult(`Restored ${ok}${fail ? `, ${fail} failed` : ""}.`);
    bulk.clear();
    queryClient.invalidateQueries({ queryKey: ["products", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["dashboard", org?.id] });
  };

  const runBulkReorder = async () => {
    setBulkBusy(true);
    setBulkResult("");
    try {
      const rp = await productApi.bulkUpdate(org!.id, bulk.selectedIds, "reorder_point", { reorder_point: Number(bulkReorderPoint) || 0 });
      const rq = await productApi.bulkUpdate(org!.id, bulk.selectedIds, "reorder_qty", { reorder_qty: Number(bulkReorderQty) || 0 });
      setBulkResult(`Reorder point updated: ${rp.succeeded}. Reorder qty updated: ${rq.succeeded}.`);
    } catch (e) {
      setBulkResult((e as Error).message);
    }
    setBulkBusy(false);
    bulk.clear();
    queryClient.invalidateQueries({ queryKey: ["products", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["dashboard", org?.id] });
  };

  const exportSelected = () => {
    const selectedSet = bulk.selected;
    const selectedRows = rows.filter((r) => selectedSet.has(r.id));
    if (selectedRows.length === 0) return;
    const headers = ["name", "reference", "sku", "barcode", "mpn", "category", "cost_price", "selling_price", "reorder_point", "reorder_qty", "status", "available"];
    const csvRows = selectedRows.map((p) => [
      p.name, p.reference ?? "", p.sku, p.barcode ?? "", p.mpn ?? "", p.categories?.name ?? "", p.cost_price ?? "", p.selling_price ?? "", p.reorder_point, p.reorder_qty, p.status, p.hasStockRecord ? p.available : "",
    ]);
    downloadCsv(`products-selected-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(headers, csvRows));
  };

  const handleExport = () => {
    if (!data) return;
    const avail: Record<string, number> = {};
    for (const b of data.balances) avail[b.product_id] = (avail[b.product_id] ?? 0) + Number(b.available ?? 0);
    const headers = ["name", "reference", "sku", "barcode", "mpn", "category", "cost_price", "selling_price", "reorder_point", "reorder_qty", "status", "available"];
    const rows = data.products.map((p) => [
      p.name,
      p.reference ?? "",
      p.sku,
      p.barcode ?? "",
      p.mpn ?? "",
      p.categories?.name ?? "",
      p.cost_price ?? "",
      p.selling_price ?? "",
      p.reorder_point,
      p.reorder_qty,
      p.status,
      p.id in avail ? avail[p.id] : "",
    ]);
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`products-${stamp}.csv`, toCsv(headers, rows));
  };

  const handleFullExport = async () => {
    if (!org) return;
    const csv = await exportProductsFullCsv(org.id);
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`products-full-${stamp}.csv`, csv);
  };

  const handleAddCategory = async (e: FormEvent) => {
    e.preventDefault();
    setCatError("");
    const name = newCatName.trim();
    if (!name) {
      setCatError("Enter a category name.");
      return;
    }
    const dup = findDuplicateCategory(data?.categories ?? [], name, newCatParentId || null);
    if (dup) {
      setCatError(
        dup.archived
          ? `A category named "${dup.name}" already exists here but is archived. Restore it instead of creating a duplicate.`
          : `A category named "${dup.name}" already exists here.`,
      );
      return;
    }
    setCatSaving(true);
    const { error: insErr } = await supabase.from("categories").insert({ org_id: org!.id, name, parent_id: newCatParentId || null });
    setCatSaving(false);
    if (insErr) {
      setCatError(insErr.message);
      return;
    }
    setNewCatName("");
    setNewCatParentId("");
    queryClient.invalidateQueries({ queryKey: ["products", org?.id] });
  };

  const handleRenameCategory = async (catId: string) => {
    if (!renameValue.trim()) return;
    setCatSaving(true);
    setCatError("");
    await supabase.from("categories").update({ name: renameValue.trim() }).eq("id", catId);
    setCatSaving(false);
    setRenamingId(null);
    setRenameValue("");
    queryClient.invalidateQueries({ queryKey: ["products", org?.id] });
  };

  const handleDeleteCategory = async (catId: string) => {
    setCatError("");
    if ((data?.categoryCounts[catId] ?? 0) > 0) {
      setCatError("This category is used by products. Reassign them first.");
      return;
    }
    setCatSaving(true);
    const { error: delErr } = await supabase.from("categories").delete().eq("id", catId);
    setCatSaving(false);
    if (delErr) { setCatError(delErr.message); return; }
    queryClient.invalidateQueries({ queryKey: ["products", org?.id] });
  };

  const handleArchiveCategory = async (catId: string, toArchived: boolean) => {
    setCatError("");
    setCatSaving(true);
    const { error: updErr } = await supabase.from("categories").update({ status: toArchived ? "archived" : "active" }).eq("id", catId);
    setCatSaving(false);
    if (updErr) { setCatError(updErr.message); return; }
    queryClient.invalidateQueries({ queryKey: ["products", org?.id] });
  };

  if (isLoading) return <ProductsLoading />;
  if (isError) {
    return (
      <div>
        <PageHeader title="Products" subtitle="Manage your product catalogue" />
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <p className="text-sm text-slate-500 mb-4">Couldn't load products.</p>
          <button onClick={() => refetch()} className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap">Retry</button>
        </div>
      </div>
    );
  }
  if (!data) return <ProductsLoading />;

  const availableByProduct: Record<string, number> = {};
  const onHandByProduct: Record<string, number> = {};
  const allocatedByProduct: Record<string, number> = {};
  for (const b of data.balances) {
    availableByProduct[b.product_id] = (availableByProduct[b.product_id] ?? 0) + Number(b.available ?? 0);
    onHandByProduct[b.product_id] = (onHandByProduct[b.product_id] ?? 0) + Number(b.on_hand ?? 0);
    allocatedByProduct[b.product_id] = (allocatedByProduct[b.product_id] ?? 0) + Number(b.allocated ?? 0);
  }

  const rows = data.products.map((p) => ({
    ...p,
    available: availableByProduct[p.id] ?? 0,
    onHand: onHandByProduct[p.id] ?? 0,
    allocated: allocatedByProduct[p.id] ?? 0,
    hasStockRecord: p.id in availableByProduct,
  }));

  // Why did this product match the current search?
  const matchReason = (p: ProductRow): string | null => {
    const term = sanitize(debouncedSearch);
    if (!term) return null;
    const t = term.toLowerCase();
    const ident = data.identifierReasons[p.id];
    if (ident) return ident;
    const variant = data.variantReasons[p.id];
    if (variant) return variant;
    if ((p.name || "").toLowerCase().includes(t)) return "Name";
    if ((p.sku || "").toLowerCase().includes(t)) return "SKU";
    if ((p.barcode || "").toLowerCase().includes(t)) return "Barcode";
    if ((p.reference || "").toLowerCase().includes(t)) return "Reference";
    if ((p.mpn || "").toLowerCase().includes(t)) return "MPN";
    return null;
  };

  const activeCategories = (data.categories ?? []).filter((c) => (c.status ?? "active") !== "archived");
  const archivedCategories = (data.categories ?? []).filter((c) => (c.status ?? "active") === "archived");
  const flatActiveCategories = flattenWithDepth(activeCategories);
  const activeSuppliers = (data.suppliers ?? []).filter((s) => s.status === "active");
  const searching = sanitize(debouncedSearch).length > 0;

  const viewModels: ProductListRowViewModel[] = rows.map((p) =>
    buildProductListRow({
      id: p.id,
      name: p.name,
      reference: p.reference,
      sku: p.sku,
      status: p.status,
      sellingPrice: p.selling_price,
      reorderPoint: Number(p.reorder_point ?? 0),
      available: p.available,
      onHand: p.onHand,
      allocated: p.allocated,
      hasStockRecord: p.hasStockRecord,
      categoryId: p.category_id ?? null,
      variantCount: data.variantByProduct[p.id]?.count ?? 0,
      variantSku: data.variantByProduct[p.id]?.sku ?? null,
      variantPrices: data.variantPricesByProduct[p.id] ?? [],
      reason: matchReason(p),
      categories: data.categories,
      brand: p.brand,
      weightKg: p.weight_kg,
    }),
  );

  const sellingConnectors = orderSellingConnectors(
    (coverageData?.connectors ?? [])
      .filter((c) => isSellingProvider(c.provider))
      .map((c) => ({
        id: c.id,
        name: connectorDisplayName({
          id: c.id,
          name: c.name,
          provider: c.provider,
          authStatus: c.auth_status,
          storeHash: coverageData?.storefront.storeHashByConnector[c.id],
          storefrontOrigin: coverageData?.storefront.originByConnector[c.id],
        }),
        provider: c.provider,
        authStatus: c.auth_status,
      })),
  );
  const badgesByProduct: Record<string, ReturnType<typeof computeChannelCoverage>> = {};
  const customUrlByProductConnector: Record<string, Record<string, string>> = {};
  for (const vm of viewModels) {
    const rows = (coverageData?.listings ?? []).filter((l) => l.product_id === vm.id);
    const listings = rows.map((l) => ({
        connectorId: l.connector_id,
        publicationStatus: l.publication_status,
        status: l.status,
        hasDirty: (l.dirty_domains?.length ?? 0) > 0,
        dirtyDomains: l.dirty_domains,
        lastError: l.last_error,
        externalListingId: l.confirmedId,
        providerConfirmedListed: Boolean(l.confirmedId),
    }));
    const paths: Record<string, string> = {};
    for (const l of rows) {
      const path = coverageData?.bcPathByListing[l.id];
      if (path) paths[l.connector_id] = path;
    }
    customUrlByProductConnector[vm.id] = paths;
    badgesByProduct[vm.id] = computeChannelCoverage(sellingConnectors, listings);
    const listingIssues = badgesByProduct[vm.id].filter((b) => b.signal === "red" || b.signal === "amber");
    for (const b of listingIssues) {
      vm.attentionReasons.push(`Listing/sync issue: ${b.name} (${b.label})`);
    }
  }

  const filteredViewModels = viewModels.filter((vm) => {
    const badges = badgesByProduct[vm.id] ?? [];
    return productMatchesChannelFilter(badges, channelFilter, {
      isDraft: vm.status === "draft",
      lowStock: vm.stockTag === "Low",
      outOfStock: vm.stockTag === "Out",
      missingRequired: !vm.name,
      attentionReasons: vm.attentionReasons,
    });
  });

  const fullyListedCount = viewModels.filter((vm) => channelCoverageSummary(badgesByProduct[vm.id] ?? []).fullyListed).length;
  const needsAttentionCount = viewModels.filter((vm) =>
    productMatchesChannelFilter(badgesByProduct[vm.id] ?? [], "needs_attention", {
      isDraft: vm.status === "draft",
      lowStock: vm.stockTag === "Low",
      outOfStock: vm.stockTag === "Out",
      attentionReasons: vm.attentionReasons,
    }),
  ).length;
  const lowStockCount = viewModels.filter((vm) => vm.stockTag === "Low").length;
  const outStockCount = viewModels.filter((vm) => vm.stockTag === "Out").length;
  const defaultLocation = coverageData?.locations?.[0] ?? null;

  return (
    <div>
      <PageHeader title="Products" subtitle="Manage your product catalogue">
        <button onClick={handleExport} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex items-center gap-2">
          <i className="ri-download-2-line" /> Export
        </button>
        <button onClick={handleFullExport} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex items-center gap-2">
          <i className="ri-download-cloud-2-line" /> Full export
        </button>
        <button onClick={() => setImportOpen(true)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex items-center gap-2">
          <i className="ri-upload-2-line" /> Import
        </button>
        <button onClick={() => { setCatError(""); setCatOpen(true); }} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex items-center gap-2">
          <i className="ri-price-tag-3-line" /> Categories
        </button>
        <button onClick={() => navigate("/products/new")} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex items-center gap-2">
          <i className="ri-add-line" />
          Add Product
        </button>
      </PageHeader>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
        {[
          ["Products", data.total],
          ["Fully listed", fullyListedCount],
          ["Needs attention", needsAttentionCount],
          ["Low stock", lowStockCount],
          ["Out of stock", outStockCount],
        ].map(([label, count]) => (
          <div key={String(label)} className="bg-white border border-slate-200 rounded-lg px-3 py-2">
            <p className="text-[11px] text-slate-500">{label}</p>
            <p className="text-lg font-semibold text-slate-800">{count}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1">
            <button
              onClick={() => { setStatusFilter("active"); setPage(0); }}
              className={`px-3 py-1.5 text-sm font-medium rounded-full whitespace-nowrap transition-colors ${statusFilter === "active" ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
            >
              Active
            </button>
            <button
              onClick={() => { setStatusFilter("archived"); setPage(0); }}
              className={`px-3 py-1.5 text-sm font-medium rounded-full whitespace-nowrap transition-colors ${statusFilter === "archived" ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
            >
              Archived
            </button>
          </div>
          <div className="relative flex-1 max-w-md min-w-[200px]">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              aria-label="Search products by name, SKU, barcode, GTIN, UPC, EAN or MPN"
              placeholder="Search name, SKU, barcode, GTIN/UPC/EAN or MPN..."
              className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
            />
          </div>
          <select
            value={catFilter}
            onChange={(e) => { setCatFilter(e.target.value); setPage(0); }}
            className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 whitespace-nowrap"
            aria-label="Filter by category"
          >
            <option value="">All categories</option>
            {flatActiveCategories.map((c) => (
              <option key={c.id} value={c.id}>{"\u00A0".repeat(c.depth * 2)}{c.name}</option>
            ))}
          </select>
          <select
            value={typeFilter}
            onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}
            className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 whitespace-nowrap"
            aria-label="Filter by product type"
          >
            <option value="">All types</option>
            <option value="physical">Physical</option>
            <option value="bundle">Bundle</option>
            <option value="digital">Digital</option>
            <option value="service">Service</option>
          </select>
          <select
            value={supplierFilter}
            onChange={(e) => { setSupplierFilter(e.target.value); setPage(0); }}
            className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 whitespace-nowrap"
            aria-label="Filter by supplier"
          >
            <option value="">All suppliers</option>
            {activeSuppliers.map((s) => (
              <option key={s.id} value={s.id}>{s.name}{s.code ? ` (${s.code})` : ""}</option>
            ))}
          </select>
          <select
            value={stockFilter}
            onChange={(e) => { setStockFilter(e.target.value as "all" | "in_stock" | "low_stock" | "out_of_stock"); setPage(0); }}
            className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 whitespace-nowrap"
            aria-label="Filter by stock state"
          >
            <option value="all">All stock states</option>
            <option value="in_stock">In stock</option>
            <option value="low_stock">Low stock</option>
            <option value="out_of_stock">Out of stock</option>
          </select>
          <select
            value={channelFilter}
            onChange={(e) => { setChannelFilter(e.target.value as CatalogueChannelFilter); setPage(0); }}
            className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 whitespace-nowrap"
            aria-label="Filter by channel coverage"
          >
            <option value="all">All channels</option>
            <option value="fully_listed">Fully listed</option>
            <option value="missing_channel">Missing a channel</option>
            <option value="not_listed">Not listed anywhere</option>
            <option value="needs_attention">Needs attention</option>
            {sellingConnectors.map((c) => (
              <option key={`listed-${c.id}`} value={`listed:${c.id}`}>Listed on {c.name}</option>
            ))}
            {sellingConnectors.map((c) => (
              <option key={`not-${c.id}`} value={`not_listed:${c.id}`}>Not listed on {c.name}</option>
            ))}
          </select>
        </div>

        {coverageData?.drafts && coverageData.drafts.length > 0 && statusFilter === "active" && (
          <div className="px-4 py-3 border-b border-slate-100 bg-amber-50/50">
            <p className="text-xs font-semibold text-amber-800 mb-2">Draft / Incomplete</p>
            <div className="flex flex-wrap gap-2">
              {coverageData.drafts.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => navigate(`/products/new?draft=${d.id}`)}
                  className="px-3 py-1.5 bg-white border border-amber-200 rounded-lg text-xs text-slate-700 hover:bg-amber-50"
                >
                  {d.title} · Resume
                </button>
              ))}
            </div>
          </div>
        )}
        {rows.length === 0 ? (
          data.total === 0 && !search.trim() && !catFilter && !typeFilter && !supplierFilter && stockFilter === "all" ? (
            statusFilter === "archived" ? (
              <EmptyState
                icon="ri-archive-line"
                title="No archived products"
                description="Archived products are hidden from active lists but keep their full history. Archive a product to find it here."
              />
            ) : (
              <EmptyState
                icon="ri-archive-2-line"
                title="No products yet"
                description="Products are the items you stock and sell. Add your first product to start tracking inventory and orders."
                action={
                  <button onClick={() => navigate("/products/new")} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2">
                    <i className="ri-add-line" /> Add your first product
                  </button>
                }
              />
            )
          ) : (
            <EmptyState title="No products found" description={searching ? "Try a different search term or clear your filters." : "Try clearing your filters."} />
          )
        ) : (
          <>
          {bulk.someSelected && (
            <div className="px-4 pt-4">
              <BulkActionBar count={bulk.selected.size} onClear={bulk.clear}>
                {canManage && statusFilter === "active" && (
                  <>
                    <button
                      onClick={() => { setBulkSellingAction("resume"); setBulkSellingOpen(true); }}
                      disabled={bulkBusy}
                      className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-xs font-medium rounded-lg whitespace-nowrap"
                    >
                      Activate
                    </button>
                    <button
                      onClick={() => { setBulkSellingAction("pause"); setBulkSellingOpen(true); }}
                      disabled={bulkBusy}
                      className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white text-xs font-medium rounded-lg whitespace-nowrap"
                    >
                      Pause
                    </button>
                    <button onClick={() => runBulk("archive")} disabled={bulkBusy} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-60 text-white text-xs font-medium rounded-lg whitespace-nowrap">
                      Archive
                    </button>
                    <button onClick={() => { setBulkCatOpen(true); setBulkCategoryId(""); }} disabled={bulkBusy} className="px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-60 text-slate-700 text-xs font-medium rounded-lg whitespace-nowrap">
                      Set category
                    </button>
                    <button onClick={() => { setBulkSupplierOpen(true); setBulkSupplierId(""); }} disabled={bulkBusy} className="px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-60 text-slate-700 text-xs font-medium rounded-lg whitespace-nowrap">
                      Set supplier
                    </button>
                    <button onClick={() => { setBulkReorderPoint(""); setBulkReorderQty(""); setBulkReorderOpen(true); }} disabled={bulkBusy} className="px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-60 text-slate-700 text-xs font-medium rounded-lg whitespace-nowrap">
                      Set reorder
                    </button>
                    <button onClick={() => { setBulkPriceValue(""); setBulkPriceOpen(true); }} disabled={bulkBusy} className="px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-60 text-slate-700 text-xs font-medium rounded-lg whitespace-nowrap">
                      Set price
                    </button>
                  </>
                )}
                {canManage && statusFilter === "archived" && (
                  <button onClick={runBulkRestore} disabled={bulkBusy} className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-xs font-medium rounded-lg whitespace-nowrap">
                    Restore
                  </button>
                )}
                <button onClick={exportSelected} className="px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-medium rounded-lg whitespace-nowrap flex items-center gap-1">
                  <i className="ri-download-2-line" /> Export selected
                </button>
              </BulkActionBar>
            </div>
          )}
          {bulkResult && <div className="px-4 pt-3 text-sm text-slate-600">{bulkResult}</div>}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[960px]">
              <thead>
                <tr className="bg-slate-50/70 border-b border-slate-100">
                  <th className="w-10 px-4 py-3">
                    <input type="checkbox" checked={bulk.allSelected} onChange={bulk.toggleAll} aria-label="Select all products" className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer" />
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Product</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">SKU / variants</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Category</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Price</th>
                  {sellingConnectors.map((c) => (
                    <th
                      key={c.id}
                      className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider min-w-[9rem] max-w-[12rem]"
                      title={c.name}
                    >
                      {c.name}
                    </th>
                  ))}
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Available</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredViewModels.map((vm) => (
                  <tr key={vm.id} onClick={() => navigate(`/products/${vm.id}`)} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors cursor-pointer">
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={bulk.selected.has(vm.id)} onChange={() => bulk.toggle(vm.id)} aria-label={`Select ${vm.name}`} className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer" />
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-slate-800">
                      <Link to={`/products/${vm.id}`} onClick={(e) => e.stopPropagation()} className="hover:text-emerald-700">
                        {vm.name}
                      </Link>
                      {vm.reference && <span className="block text-xs font-normal text-slate-400 font-mono mt-0.5">{vm.reference}</span>}
                      {vm.reason && (
                        <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[11px] font-medium rounded-full">
                          <i className="ri-search-line text-xs" />
                          {vm.reason}
                        </span>
                      )}
                      {vm.attentionReasons.length > 0 && (
                        <span className="block mt-1 text-[11px] text-amber-700" title={vm.attentionReasons.join(" · ")}>
                          {vm.attentionReasons[0]}
                          {vm.attentionReasons.length > 1 ? ` +${vm.attentionReasons.length - 1}` : ""}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500">
                      {vm.variantCount > 1 ? (
                        <span className="inline-flex items-center gap-1 text-slate-600">
                          <i className="ri-stack-line text-xs" />
                          {vm.variantCount} variants
                        </span>
                      ) : (
                        <span className="font-mono">{vm.variantSku ?? vm.productSku ?? "—"}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{vm.categoryPath || "—"}</td>
                    <td
                      className="px-4 py-3 text-sm text-slate-700 font-medium whitespace-nowrap cursor-pointer"
                      data-testid="quick-price-cell"
                      title="Edit variant prices"
                      onClick={(e) => { e.stopPropagation(); setQuickPrices({ productId: vm.id, name: vm.name }); }}
                    >
                      {vm.price.min == null ? (
                        <span className="text-slate-400">—</span>
                      ) : vm.price.min === vm.price.max ? (
                        money(vm.price.min)
                      ) : (
                        `${money(vm.price.min)} – ${money(vm.price.max)}`
                      )}
                    </td>
                    {(badgesByProduct[vm.id] ?? []).map((badge) => (
                      <td key={badge.connectorId} className="px-3 py-3 align-top">
                        <ChannelMatrixCell
                          badge={badge}
                          productId={vm.id}
                          storefrontOrigin={coverageData?.storefront.originByConnector[badge.connectorId]}
                          customUrl={customUrlByProductConnector[vm.id]?.[badge.connectorId]}
                        />
                      </td>
                    ))}
                    <td className="px-4 py-3 cursor-pointer" data-testid="quick-stock-cell" onClick={(e) => { e.stopPropagation(); setQuickStock({ productId: vm.id, name: vm.name, available: vm.available, variantCount: vm.variantCount }); }}>
                      {vm.stockCell === "untracked" ? (
                        <span className="text-sm text-slate-400" title="No stock records — import opening stock or adjust stock to begin tracking">Not tracked</span>
                      ) : (
                        <>
                          <span className={`text-sm font-medium ${vm.stockTone}`}>{vm.stockPrimary}</span>
                          <span className="block text-[11px] text-slate-400 mt-0.5">{vm.stockSecondary}</span>
                          {vm.stockTag && (
                            <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${vm.stockTag === "Out" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>
                              {vm.stockTag}
                            </span>
                          )}
                        </>
                      )}
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={vm.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pageSize={pageSize} total={data.total} onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(0); }} />
          </>
        )}
      </div>

      <Modal open={catOpen} onClose={() => setCatOpen(false)} title="Manage Categories">
        <div className="space-y-4">
          {catError && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{catError}</div>}
          <form onSubmit={handleAddCategory} className="space-y-2">
            <input
              value={newCatName}
              onChange={(e) => setNewCatName(e.target.value)}
              placeholder="New category name (e.g. T-Shirts)"
              className={inputCls}
            />
            <select value={newCatParentId} onChange={(e) => setNewCatParentId(e.target.value)} className={inputCls}>
              <option value="">Top level (no parent)</option>
              {flatActiveCategories.map((c) => (
                <option key={c.id} value={c.id}>{"\u00A0".repeat(c.depth * 2)}{c.name}</option>
              ))}
            </select>
            <button type="submit" disabled={catSaving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
              Add category
            </button>
          </form>

          <div className="border-t border-slate-100 pt-3">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Active</p>
            <div className="divide-y divide-slate-100 max-h-64 overflow-y-auto">
              {flatActiveCategories.map((c) => (
                <div key={c.id} className="py-1.5 flex items-center justify-between gap-3">
                  {renamingId === c.id ? (
                    <div className="flex-1 flex gap-2">
                      <input
                        autoFocus
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") handleRenameCategory(c.id); }}
                        className={inputCls}
                      />
                      <button onClick={() => handleRenameCategory(c.id)} className="px-3 py-2 bg-emerald-600 text-white text-xs rounded-lg whitespace-nowrap">Save</button>
                      <button onClick={() => { setRenamingId(null); setRenameValue(""); }} className="px-3 py-2 bg-white border border-slate-200 text-xs rounded-lg whitespace-nowrap">Cancel</button>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm font-medium text-slate-800 truncate">{"\u00A0".repeat(c.depth * 2)}{c.name}</span>
                        <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full shrink-0">{data?.categoryCounts[c.id] ?? 0} products</span>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => { setRenamingId(c.id); setRenameValue(c.name); setCatError(""); }} title="Rename" className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700">
                          <i className="ri-pencil-line" />
                        </button>
                        <button onClick={() => handleArchiveCategory(c.id, true)} title="Archive" className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700">
                          <i className="ri-archive-line" />
                        </button>
                        <button onClick={() => handleDeleteCategory(c.id)} title="Delete" className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-500">
                          <i className="ri-delete-bin-line" />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
              {flatActiveCategories.length === 0 && (
                <div className="py-6 text-center text-sm text-slate-400">
                  No categories yet — type a name above and press <strong>Add category</strong>.
                </div>
              )}
            </div>
          </div>

          {archivedCategories.length > 0 && (
            <div className="border-t border-slate-100 pt-3">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Archived</p>
              <div className="divide-y divide-slate-100 max-h-48 overflow-y-auto">
                {archivedCategories.map((c) => (
                  <div key={c.id} className="py-1.5 flex items-center justify-between gap-3">
                    <span className="text-sm text-slate-400">{c.name}</span>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => handleArchiveCategory(c.id, false)} className="px-2 py-1 text-xs font-medium text-emerald-600 hover:text-emerald-700 whitespace-nowrap">Restore</button>
                      <button onClick={() => handleDeleteCategory(c.id)} title="Delete" className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-500">
                        <i className="ri-delete-bin-line" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <p className="text-xs text-slate-400">Categories can be nested, e.g. Clothing → Men's Clothing → T-Shirts. Archived categories stay on their products but are hidden from new assignments.</p>
        </div>
      </Modal>

      <Modal
        open={bulkSellingOpen}
        onClose={() => { if (!bulkBusy) setBulkSellingOpen(false); }}
        title={bulkSellingAction === "pause" ? "Pause selling selected products?" : "Resume selling selected products?"}
        footer={
          <>
            <button
              onClick={() => setBulkSellingOpen(false)}
              disabled={bulkBusy}
              className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              onClick={() => void runBulkSelling()}
              disabled={bulkBusy}
              className="px-4 py-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white text-sm font-medium rounded-lg whitespace-nowrap"
            >
              {bulkBusy
                ? (bulkSellingAction === "pause" ? "Pausing..." : "Resuming...")
                : (bulkSellingAction === "pause" ? "Pause selling" : "Resume selling")}
            </button>
          </>
        }
      >
        <div className="space-y-3 text-sm text-slate-600">
          {bulkSellingAction === "pause" ? (
            <>
              <p>This will temporarily stop the selected products from being sold on all of their currently published channels.</p>
              <p>Products, variants, inventory, prices, marketplace mappings and history will remain unchanged.</p>
            </>
          ) : (
            <>
              <p>Tradivo will attempt to resume each selected product only on the channels that were active before that product was paused.</p>
              <p>Any channel that cannot be updated will be reported instead of being silently treated as resumed.</p>
            </>
          )}
        </div>
      </Modal>

      <Modal
        open={bulkCatOpen}
        onClose={() => setBulkCatOpen(false)}
        title="Set category for selected products"
        footer={
          <>
            <button onClick={() => setBulkCatOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">
              Cancel
            </button>
            <button
              onClick={() => { runBulk("set_category", { category_id: bulkCategoryId }); setBulkCatOpen(false); }}
              disabled={bulkBusy || !bulkCategoryId}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap"
            >
              {bulkBusy ? "Applying..." : "Apply to selected"}
            </button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            Apply a category to <span className="font-medium text-slate-800">{bulk.selected.size}</span> selected product{bulk.selected.size === 1 ? "" : "s"}.
          </p>
          <select value={bulkCategoryId} onChange={(e) => setBulkCategoryId(e.target.value)} className={inputCls}>
            <option value="">Select category...</option>
            {flatActiveCategories.map((c) => (
              <option key={c.id} value={c.id}>{"\u00A0".repeat(c.depth * 2)}{c.name}</option>
            ))}
          </select>
        </div>
      </Modal>

      <Modal
        open={bulkSupplierOpen}
        onClose={() => setBulkSupplierOpen(false)}
        title="Assign supplier to selected products"
        footer={
          <>
            <button onClick={() => setBulkSupplierOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">
              Cancel
            </button>
            <button
              onClick={() => { runBulk("assign_supplier", { supplier_id: bulkSupplierId }); setBulkSupplierOpen(false); }}
              disabled={bulkBusy || !bulkSupplierId}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap"
            >
              {bulkBusy ? "Applying..." : "Assign supplier"}
            </button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            Link a supplier to <span className="font-medium text-slate-800">{bulk.selected.size}</span> selected product{bulk.selected.size === 1 ? "" : "s"}. Products already linked to this supplier are left unchanged.
          </p>
          <select value={bulkSupplierId} onChange={(e) => setBulkSupplierId(e.target.value)} className={inputCls}>
            <option value="">Select supplier...</option>
            {activeSuppliers.map((s) => (
              <option key={s.id} value={s.id}>{s.name}{s.code ? ` (${s.code})` : ""}</option>
            ))}
          </select>
          {activeSuppliers.length === 0 && (
            <p className="text-xs text-slate-400">No active suppliers yet — add one under Suppliers first.</p>
          )}
        </div>
      </Modal>

      <Modal
        open={bulkReorderOpen}
        onClose={() => setBulkReorderOpen(false)}
        title="Set reorder settings for selected products"
        footer={
          <>
            <button onClick={() => setBulkReorderOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">Cancel</button>
            <button
              onClick={() => { runBulkReorder(); setBulkReorderOpen(false); }}
              disabled={bulkBusy || (!bulkReorderPoint && !bulkReorderQty)}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap"
            >
              {bulkBusy ? "Applying..." : "Apply to selected"}
            </button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            Update reorder settings for <span className="font-medium text-slate-800">{bulk.selected.size}</span> selected product{bulk.selected.size === 1 ? "" : "s"}. Leave a field blank to keep its current value.
          </p>
          <div>
            <label className={labelCls}>Reorder point (low-stock threshold)</label>
            <input type="number" min="0" step="1" value={bulkReorderPoint} onChange={(e) => setBulkReorderPoint(e.target.value)} className={inputCls} placeholder="e.g. 10" />
          </div>
          <div>
            <label className={labelCls}>Reorder quantity (suggested order size)</label>
            <input type="number" min="0" step="1" value={bulkReorderQty} onChange={(e) => setBulkReorderQty(e.target.value)} className={inputCls} placeholder="e.g. 50" />
          </div>
        </div>
      </Modal>

      <Modal
        open={bulkPriceOpen}
        onClose={() => setBulkPriceOpen(false)}
        title="Set price for selected products"
        footer={
          <>
            <button onClick={() => setBulkPriceOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">Cancel</button>
            <button
              onClick={() => { runBulk(bulkPriceMode === "percentage" ? "price_percentage" : "price_fixed", { price_value: Number(bulkPriceValue) }); setBulkPriceOpen(false); }}
              disabled={bulkBusy || !bulkPriceValue}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap"
            >
              {bulkBusy ? "Applying..." : "Apply to selected"}
            </button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            Update price for <span className="font-medium text-slate-800">{bulk.selected.size}</span> selected product{bulk.selected.size === 1 ? "" : "s"}.
          </p>
          <div className="flex gap-2">
            <button onClick={() => setBulkPriceMode("percentage")} className={`px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${bulkPriceMode === "percentage" ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600"}`}>Percentage (%)</button>
            <button onClick={() => setBulkPriceMode("fixed")} className={`px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${bulkPriceMode === "fixed" ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600"}`}>Fixed price</button>
          </div>
          <div>
            <label className={labelCls}>{bulkPriceMode === "percentage" ? "Percentage change" : "New price"}</label>
            <input type="number" step="0.01" value={bulkPriceValue} onChange={(e) => setBulkPriceValue(e.target.value)} className={inputCls} placeholder={bulkPriceMode === "percentage" ? "e.g. 10 for +10%" : "e.g. 45.00"} />
            {bulkPriceMode === "percentage" && <p className="text-xs text-slate-400 mt-1">Positive to increase, negative to decrease.</p>}
          </div>
        </div>
      </Modal>

      <StagedImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title="Import products"
        description="Review every row before anything is written. Enter real GTINs only — never invent barcodes or type N/A. Leave unknown cells blank. weight_kg / length_cm / width_cm / height_cm are package (shipping) measurements, not item-only size. Material uses Attribute:Material (no separate material column). cost_price and supplier_* stay internal Tradivo data and are not marketplace-eligible. Do not import Amazon product types, ASINs, or browse nodes here. identifiers_not_applicable means the product has no barcode in Tradivo — it is not Amazon exemption."
        requiredColumns={["name"]}
        optionalColumns={["reference", "sku", "barcode", "mpn", "brand", "manufacturer", "condition", "country_of_origin", "weight_kg", "length_cm", "width_cm", "height_cm", "package_weight_kg", "package_length_cm", "package_width_cm", "package_height_cm", "gtin", "upc", "ean", "identifiers_not_applicable", "category", "cost_price", "selling_price", "reorder_point", "reorder_qty", "description", "variant_sku", "variant_barcode", "variant_gtin", "variant_upc", "variant_ean", "Option:Colour", "Option:Size", "Option:Gender", "option_name", "option_value", "variant_cost", "variant_price", "Attribute:Material", "supplier_name", "supplier_sku", "supplier_cost", "supplier_moq", "supplier_order_multiple", "supplier_lead_time", "supplier_preferred"]}
        templateHeaders={["name", "sku", "brand", "manufacturer", "mpn", "condition", "country_of_origin", "weight_kg", "length_cm", "width_cm", "height_cm", "gtin", "Attribute:Material", "cost_price", "selling_price", "description", "variant_sku", "Option:Colour", "Option:Size", "variant_gtin", "variant_cost", "variant_price", "supplier_name"]}
        templateRows={[
          ["Padded Bridle", "BRIDLE-PVC", "Global", "", "", "new", "", "0.40", "20", "10", "5", "", "PVC", "12.00", "51.99", "Padded bridle with rubber reins", "BRIDLE-TEAL-PONY", "Teal", "Pony", "", "12.00", "51.99", ""],
          ["Padded Bridle", "BRIDLE-PVC", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "BRIDLE-TEAL-COB", "Teal", "Cob", "", "12.00", "51.99", ""],
          ["Padded Dog Collar", "COLLAR-PAD", "Global", "", "", "new", "", "0.12", "15", "8", "3", "", "", "4.50", "19.95", "Premium padded dog collar", "COLLAR-BLK-S", "Black", "S", "", "4.50", "19.95", ""],
          ["Padded Dog Collar", "COLLAR-PAD", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "COLLAR-BLK-M", "Black", "M", "", "4.50", "19.95", ""],
        ]}
        onParse={(rows, headers) => parseProductsCsv(rows, headers, org!.id)}
        onExecute={(rows) => executeProductsImport(org!.id, rows)}
        onDone={() => {
          queryClient.invalidateQueries({ queryKey: ["products", org?.id] });
          queryClient.invalidateQueries({ queryKey: ["dashboard", org?.id] });
        }}
      />
      {quickPrices && org && (
        <QuickVariantPriceModal
          open={!!quickPrices}
          onClose={() => setQuickPrices(null)}
          orgId={org.id}
          productId={quickPrices.productId}
          productName={quickPrices.name}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ["products", org.id] });
            queryClient.invalidateQueries({ queryKey: ["catalogue-coverage", org.id] });
          }}
        />
      )}
      {quickStock && org && (
        <QuickStockModal
          open={!!quickStock}
          onClose={() => setQuickStock(null)}
          orgId={org.id}
          productId={quickStock.productId}
          productName={quickStock.name}
          locationId={defaultLocation?.id ?? null}
          locationName={defaultLocation?.name ?? null}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ["products", org.id] });
            queryClient.invalidateQueries({ queryKey: ["catalogue-coverage", org.id] });
          }}
        />
      )}
    </div>
  );
}